'use client'

import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useUpdateTask, useDeleteTask } from './write-spec'
// Same read as the feed's, on Today.
// eslint-disable-next-line import/no-restricted-paths
import { usePeople } from '@/legacy/supabase-store/use-people'
import { useTopics } from '@/topics'
import {
  resolveLinkedPeople,
  useDocumentOps,
  useDocumentView,
} from '@/document'
import { toggleChecklistItem } from './checklist'
import { TaskRow } from './row'
import { TEXT_STYLES } from '@/ui/design-tokens'
import type { ChecklistItem, TaskEntry } from './meta'
import type { TaskDraft } from './write-spec'
import type { Linked } from '../types'
import type { Entry, EntryWithPeople } from '@/document'

interface TodayTasksProps {
  overdueTasks: Entry[]
  dueTodayTasks: Entry[]
}

/**
 * TodayTasks — shows overdue tasks first, then tasks due today.
 * Only tasks with an explicit due date appear here.
 */
export function TodayTasks({ overdueTasks, dueTodayTasks }: TodayTasksProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [completingId, setCompletingId] = useState<string | null>(null)
  const [checklistUpdates, setChecklistUpdates] = useState<
    Record<string, ChecklistItem[]>
  >({})
  const completingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  useEffect(() => {
    return () => {
      if (completingTimeoutRef.current)
        clearTimeout(completingTimeoutRef.current)
    }
  }, [])

  const doc = useDocumentView()
  const ops = useDocumentOps()
  const { data: availablePeople = [] } = usePeople()
  const { topics: allTopics } = useTopics()
  const availableTopics = useMemo(
    () =>
      allTopics
        .filter((t) => t.entryType === 'task')
        .map((t) => ({ id: t.id, name: t.name })),
    [allTopics],
  )

  const updateTask = useUpdateTask()
  const deleteTask = useDeleteTask()

  // Resolve people and topics onto raw Entry objects for TaskRow compatibility
  const resolveEntry = useCallback(
    (entry: Entry): EntryWithPeople => {
      if (!doc) {
        return { ...entry, people: [], topics: [] }
      }
      const linkedPeople = resolveLinkedPeople(entry.id, doc)
      const people = linkedPeople.map((p) => ({
        id: p.id,
        name: p.title,
      }))
      const topics = (entry.topicIds || [])
        .map((topicId) => doc.topics[topicId])
        .filter((topic) => topic)
        .map((topic) => ({
          id: topic.id,
          name: topic.name,
        }))
      return { ...entry, people, topics }
    },
    [doc],
  )

  // All raw tasks for checklist lookups
  const allTasks = useMemo(
    () => [...overdueTasks, ...dueTodayTasks],
    [overdueTasks, dueTodayTasks],
  )

  // Resolved task lists
  const resolvedOverdue = useMemo(
    () => overdueTasks.map(resolveEntry),
    [overdueTasks, resolveEntry],
  )
  const resolvedToday = useMemo(
    () => dueTodayTasks.map(resolveEntry),
    [dueTodayTasks, resolveEntry],
  )

  const handleComplete = useCallback(
    (taskId: string) => {
      setCompletingId(taskId)
      ops.updateEntry(taskId, {
        metadata: { status: 'done', completedAt: new Date().toISOString() },
      })
      completingTimeoutRef.current = setTimeout(
        () => setCompletingId(null),
        600,
      )
    },
    [ops],
  )

  const handleDelete = useCallback(
    (taskId: string) => {
      setDeletingId(taskId)
      deleteTask.mutate(taskId, {
        onSuccess: () => {
          setDeletingId(null)
          setExpandedId((current) => (current === taskId ? null : current))
        },
        onError: (error) => {
          console.error('Failed to delete task:', error)
          setDeletingId(null)
        },
      })
    },
    [deleteTask],
  )

  const handleSave = useCallback(
    async (taskId: string, updates: Partial<TaskDraft>) => {
      setSavingId(taskId)
      return new Promise<void>((resolve, reject) => {
        updateTask.mutate(
          {
            id: taskId,
            data: updates,
          },
          {
            onSuccess: () => {
              setSavingId(null)
              if (updates.checklist !== undefined) {
                setChecklistUpdates((prev) => {
                  const newState = { ...prev }
                  delete newState[taskId]
                  return newState
                })
              }
              resolve()
            },
            onError: (error) => {
              console.error('Failed to save task:', error)
              setSavingId(null)
              reject(error)
            },
          },
        )
      })
    },
    [updateTask],
  )

  const handleChecklistToggle = useCallback(
    (taskId: string, itemId: string, checked: boolean) => {
      let updatedItems: ChecklistItem[] | undefined

      setChecklistUpdates((prev) => {
        const task = allTasks.find((t) => t.id === taskId)
        if (!task) return prev
        const currentItems =
          prev[taskId] ??
          (task.metadata as { checklist?: ChecklistItem[] }).checklist ??
          []
        updatedItems = toggleChecklistItem(currentItems, itemId, checked)
        return { ...prev, [taskId]: updatedItems }
      })

      if (!updatedItems) return

      updateTask.mutate(
        { id: taskId, data: { checklist: updatedItems } },
        {
          onError: (error) => {
            setChecklistUpdates((prev) => {
              const newState = { ...prev }
              delete newState[taskId]
              return newState
            })
            console.error('Failed to update checklist:', error)
          },
        },
      )
    },
    [allTasks, updateTask],
  )

  const getChecklistItems = useCallback(
    (taskId: string): ChecklistItem[] | null => {
      if (checklistUpdates[taskId]) return checklistUpdates[taskId]
      const task = allTasks.find((t) => t.id === taskId)
      if (!task) return null
      return (
        (task.metadata as { checklist?: ChecklistItem[] }).checklist ?? null
      )
    },
    [checklistUpdates, allTasks],
  )

  const toggleExpand = useCallback((id: string) => {
    setExpandedId((current) => (current === id ? null : id))
  }, [])

  const peopleList = useMemo(
    () => availablePeople.map((p) => ({ id: p.id, name: p.name })),
    [availablePeople],
  )

  const isEmpty = overdueTasks.length === 0 && dueTodayTasks.length === 0

  if (isEmpty) {
    return (
      <div data-testid="today-tasks" className="py-16 text-center">
        <p className="text-muted-foreground font-logo text-lg italic">
          Nothing due today
        </p>
      </div>
    )
  }

  const renderTaskRow = (task: EntryWithPeople) => {
    return (
      <TaskRow
        key={task.id}
        entry={task as Linked<TaskEntry>}
        isExpanded={expandedId === task.id}
        onToggle={() => toggleExpand(task.id)}
        onDelete={() => handleDelete(task.id)}
        onSave={(updates) => handleSave(task.id, updates)}
        isDeleting={deletingId === task.id}
        isSaving={savingId === task.id}
        context="upcoming-today"
      />
    )
  }

  return (
    <div data-testid="today-tasks">
      {resolvedOverdue.length > 0 && (
        <section aria-label="Overdue tasks">
          <h3 className={`${TEXT_STYLES.sectionHeader} mb-2 px-2`}>Overdue</h3>
          <div className="space-y-0">{resolvedOverdue.map(renderTaskRow)}</div>
        </section>
      )}

      {resolvedToday.length > 0 && (
        <section
          aria-label="Due today"
          className={resolvedOverdue.length > 0 ? 'mt-6' : undefined}
        >
          <h3 className={`${TEXT_STYLES.sectionHeader} mb-2 px-2`}>Today</h3>
          <div className="space-y-0">{resolvedToday.map(renderTaskRow)}</div>
        </section>
      )}
    </div>
  )
}
